import clsx from "clsx";
import { graphql } from "gatsby";
import React, { DetailedHTMLProps, FC, HTMLAttributes, PropsWithChildren } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { CommonColors } from "../../../types/colors";
import { PillProps } from "../../forms";
import { HeroBannerAnchors } from "./hero-banner-anchors";
import { HeroBannerMainContent } from "./hero-banner-main-content";
import { colorScheme } from "./styles";
import { HeroBannerVariants } from "./types";

export interface HeroBannerProps
  extends Omit<Queries.HeroBannerDataFragment, "__typename">,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  tagVariant?: HeroBannerVariants;
  pillTags?: Pick<PillProps, "color" | "children">[];
  customCard?: React.ReactNode;
  forceAspectRatio?: boolean;
  backgroundImageUrl?: string;
  mediaFit?: string | null;
  isProduct?: boolean;
}

export const HeroBanner: FC<PropsWithChildren<HeroBannerProps>> = ({
  children,
  forceAspectRatio,
  className,
  ...props
}) => {
  const { xs: isClientSide } = useBreakpoints();
  const { color: _color, padded, anchors, showBiesseLogo, isProduct = false } = props;
  const color = (_color as CommonColors) ?? CommonColors.Light;
  const { backgroundColor } = colorScheme[color];
  return (
    <div
      className={clsx(
        backgroundColor,
        "relative flex flex-col w-full sm:min-h-128 max-h-screen",
        padded && "px-5 pb-8 pt-14 lg:p-14",
        isProduct ? "min-h-auto" : "min-h-screen",
        (forceAspectRatio || showBiesseLogo) && " sm:aspect-[16/9]",
        className
      )}
    >
      {/* Main banner content (text, media, etc.) */}
      <HeroBannerMainContent {...props} />
      {/* Anchors on bottom */}
      {isClientSide && Boolean(anchors?.length) && (
        <HeroBannerAnchors anchors={Array.from(anchors ?? [])} color={color} />
      )}
      {children}
    </div>
  );
};

export const query = graphql`
  fragment HeroBannerData on ContentfulHeroBanner {
    __typename
    showBiesseLogo
    title
    color
    padded
    abstract {
      text: abstract
    }
    media {
      mimeType
      gatsbyImageData
      placeholderUrl
      url
    }
    card {
      ...ContactCardData
    }
    justifyText
    anchors {
      ...AnchorData
    }
  }
`;
